import React, { useState, useEffect, useRef } from 'react';
import './Projects.css';
import {
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaImages,
  FaMapMarkerAlt,
  FaVolumeUp,
  FaVolumeMute,
  FaClock,
} from 'react-icons/fa';
import projectImages, { projectCovers } from '../utils/projectImages';
import { resolveGallerySlug } from '../utils/projectGallerySlug';
import { getProjectGalleryVideo, getVideoMimeType } from '../utils/projectVideos';
import { FEATURED_PROJECTS, findFirestoreMatch } from '../utils/featuredProjects';
import Gallery from './Gallery';
import News from './News';
import GigaProject from '../components/GigaProject';
import ProjectGallery from './ProjectGallery';
import { MobileScrollRail } from './MobileScrollRail';
import { fetchProjects } from '../firebase/firestoreService';
import studioLogo from '../assets/LOGO.png';

function buildFeaturedList(firestoreProjects = []) {
  const usedIds = new Set();

  // Array order in FEATURED_PROJECTS is canonical (newest → oldest, including within-season).
  // Do not re-sort by sortKey — descending keys reverse within-season order (e.g. Karting before Rasha).
  return FEATURED_PROJECTS
    .map((featured) => {
      const cms = findFirestoreMatch(featured, firestoreProjects, usedIds);
      if (cms?.id) usedIds.add(cms.id);

      const slug = featured.slug;
      const cmsImages = cms?.images?.filter(Boolean) || [];
      const localImgs = projectImages[slug] || [];
      const images = cmsImages.length > 0 ? cmsImages : localImgs;
      const image =
        (cms?.images && cms.images[0]) ||
        cms?.imageUrl ||
        images[0] ||
        projectCovers[slug] ||
        null;

      return {
        id: cms?.id || featured.id,
        title: featured.title,
        description: cms?.text || featured.description,
        // Keep canonical season labels (e.g. "2026 – Present") over CMS year strings
        year: featured.year,
        cityOrSeason: featured.cityOrSeason || cms?.cityOrSeason || '',
        slug,
        image,
        images,
        video: getProjectGalleryVideo(slug),
        path: cms?.link || null,
        explore: cms?.link || null,
        sortKey: featured.sortKey,
      };
    });
}

function projectHasGallery(project) {
  const slug = resolveGallerySlug(project);
  const bundled = slug ? (projectImages[slug]?.length ?? 0) : 0;
  const remote = Array.isArray(project.images) ? project.images.filter(Boolean).length : 0;
  return bundled > 0 || remote > 0;
}

function toGalleryProject(project) {
  return {
    title: project.title,
    location: project.cityOrSeason || project.location || '',
    slug: resolveGallerySlug(project) || undefined,
    images: project.images,
  };
}

const caseStudies = [
  { img: projectCovers['curse-of-the-nile'], title: 'Curse of the Nile', location: 'Riyadh Season, KSA', slug: 'curse-of-the-nile' },
  { img: projectCovers.deadzone, title: 'DeadZone Experience', location: 'Dammam Global City, KSA', slug: 'deadzone' },
  { img: projectCovers.skywar, title: 'Runway Sky Siege', location: 'Runway, KSA', slug: 'skywar' },
  { img: projectCovers['italian-hotel'], title: "L'Albergo del Terrore", location: 'Riyadh Season, KSA', slug: 'italian-hotel' },
  { img: projectCovers.fnaf, title: "Five Nights at Freddy's", location: 'City Walk, 2023 Jeddah Season', slug: 'fnaf' },
  { img: projectCovers['midnight-manor'], title: 'Midnight Manor', location: 'BLVD World, Riyadh Season, KSA', slug: 'midnight-manor' },
  { img: projectCovers['a-lot-like-life-award'], title: 'Alot Like Life', location: 'BLVD City, Riyadh Season, KSA', slug: 'a-lot-like-life' },
];

const Projects = () => {
  const [videoSrc, setVideoSrc] = useState(null);
  const [activeGallery, setActiveGallery] = useState(null);
  const [visibleElements, setVisibleElements] = useState(new Set());
  const [projectList, setProjectList] = useState([]);
  const [cardMuted, setCardMuted] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mediaReady, setMediaReady] = useState({});
  const scrollContainerRef = useRef(null);

  const markMediaReady = (projectId) => {
    setMediaReady((prev) => (prev[projectId] ? prev : { ...prev, [projectId]: true }));
  };

  const observerRef = useRef();

  // Canonical featured list (all season projects), enriched from Firestore when available
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const projects = await fetchProjects();
        setProjectList(buildFeaturedList(projects));
      } catch (err) {
        console.error('Error loading projects:', err);
        // Still show the full inventory if CMS is unreachable
        setProjectList(buildFeaturedList([]));
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    // Only observe elements after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach(el => observerRef.current.observe(el));
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [projectList]); // Re-run when projectList changes

  const openVideo = (src) => setVideoSrc(src);
  const closeVideo = () => setVideoSrc(null);

  const toggleMute = (projectId, e) => {
    e.stopPropagation();
    e.preventDefault();
    const video = document.getElementById(`video-${projectId}`);
    if (video) {
      video.muted = !video.muted;
      video.volume = 0.08;
      setCardMuted(prev => ({
        ...prev,
        [projectId]: video.muted
      }));
    }
  };

  // Play the two most-visible reels. Others stay paused on a thumbnail frame (never blank "Loading").
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !projectList.length) return undefined;

    let settleTimer = null;
    let scrolling = false;

    function ensureSrc(video) {
      const pendingSrc = video.dataset.videoSrc;
      if (!pendingSrc) return;
      video.volume = 0.08;
      if (video.getAttribute('src') !== pendingSrc) {
        video.src = pendingSrc;
      }
    }

    function showThumbnail(video) {
      ensureSrc(video);
      video.pause();
      // Seek slightly so the element paints a frame instead of a blank canvas
      const paintFrame = () => {
        if (video.readyState >= 2 && video.currentTime < 0.05) {
          try {
            video.currentTime = 0.1;
          } catch {
            /* ignore seek errors before metadata */
          }
        }
      };
      if (video.readyState >= 2) paintFrame();
      else video.addEventListener('loadeddata', paintFrame, { once: true });
    }

    function activateVideo(video) {
      ensureSrc(video);
      video.play().catch(() => {});
    }

    function unloadFarVideo(video) {
      video.pause();
      // Keep poster attribute; only drop the heavy buffer when fully off-screen
      if (video.getAttribute('src')) {
        video.removeAttribute('src');
        video.load();
      }
    }

    function syncActiveVideos() {
      const videos = Array.from(
        container.querySelectorAll('.project-image-container video[data-video-src]')
      );
      if (!videos.length) return;

      const containerRect = container.getBoundingClientRect();
      const ranked = videos
        .map((video) => {
          const card = video.closest('.project-card') || video;
          const rect = card.getBoundingClientRect();
          const overlap = Math.max(
            0,
            Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left)
          );
          return { video, ratio: overlap / Math.max(rect.width, 1) };
        })
        .sort((a, b) => b.ratio - a.ratio);

      const playing = new Set(
        ranked.filter((item) => item.ratio > 0.35).slice(0, 2).map((item) => item.video)
      );

      ranked.forEach(({ video, ratio }) => {
        if (playing.has(video)) activateVideo(video);
        else if (ratio > 0) showThumbnail(video);
        else unloadFarVideo(video);
      });
    }

    function onScroll() {
      if (!scrolling) {
        scrolling = true;
        container
          .querySelectorAll('.project-image-container video[data-video-src]')
          .forEach((video) => {
            video.pause();
          });
      }
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        scrolling = false;
        syncActiveVideos();
      }, 140);
    }

    container.addEventListener('scroll', onScroll, { passive: true });
    syncActiveVideos();

    return () => {
      window.clearTimeout(settleTimer);
      container.removeEventListener('scroll', onScroll);
      container
        .querySelectorAll('.project-image-container video[data-video-src]')
        .forEach(unloadFarVideo);
    };
  }, [projectList]);

  const scroll = (direction) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const card = el.querySelector('.project-card');
    if (!card) return;
    const styles = window.getComputedStyle(el);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 28;
    const amount = card.getBoundingClientRect().width + gap;
    el.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  };

  return (
    <div className="projects-page">
      {/* Hero + Featured Projects (single section) */}
      <section className="projects-intro-section" aria-labelledby="projects-hero-title">
        <header className="projects-hero-block">
          <div className="hero-content">
            <div className={`hero-logo-mark animate-on-scroll ${visibleElements.has('projects-hero-logo') ? 'animate-fade-in-down' : ''}`} id="projects-hero-logo">
              <img src={studioLogo} alt="Studio 13" />
            </div>
            <h1 className={`animate-on-scroll ${visibleElements.has('projects-hero-title') ? 'animate-fade-in-down' : ''}`} id="projects-hero-title">Our Projects</h1>
            <p className={`hero-subtitle animate-on-scroll ${visibleElements.has('projects-hero-subtitle') ? 'animate-fade-in-up' : ''}`} id="projects-hero-subtitle">Immersive Horror Experiences & Interactive Entertainment</p>
          </div>
        </header>

        <div className="featured-projects-block">
          <div className="container">
            <div className={`section-header animate-on-scroll ${visibleElements.has('featured-header') ? 'animate-fade-in-up' : ''}`} id="featured-header">
              <span className="section-tag">Portfolio</span>
              <h2>Featured Projects</h2>
              <div className="section-divider"></div>
            </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading projects...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button
                className="retry-btn"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : projectList.length > 0 ? (
            <div className={`projects-container animate-on-scroll ${visibleElements.has('projects-grid') ? 'animate-fade-in-up' : ''}`} id="projects-grid">
              <button className="scroll-arrow scroll-left" onClick={() => scroll('left')} aria-label="Scroll left">
                <FaChevronLeft />
              </button>

              <div className="projects-cards" ref={scrollContainerRef}>
                {projectList.map((project) => {
                  const currentImage = project.image;
                  const showViewGallery = projectHasGallery(project);
                  const openProjectGallery = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveGallery(toGalleryProject(project));
                  };

                  return (
                    <div key={project.id} className="project-card">
                      <div className="project-image-container">
                        {project.video ? (
                          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                            {/* Poster / paused frame stay visible — never flash a Loading state on reels */}
                            {currentImage ? (
                              <img
                                src={currentImage}
                                alt=""
                                aria-hidden="true"
                                className="project-image project-video-poster"
                              />
                            ) : null}
                            <video
                              id={`video-${project.id}`}
                              data-video-src={project.video}
                              muted={cardMuted[project.id] !== false}
                              loop
                              playsInline
                              preload="metadata"
                              poster={currentImage || undefined}
                              className="project-image"
                              style={{
                                objectFit: 'cover',
                                width: '100%',
                                height: '100%',
                                position: currentImage ? 'absolute' : 'relative',
                                inset: currentImage ? 0 : undefined,
                                background: '#0a0a0a',
                              }}
                              onLoadedData={() => markMediaReady(project.id)}
                            />
                            <button
                              type="button"
                              className="video-mute-toggle-btn"
                              onClick={(e) => toggleMute(project.id, e)}
                              aria-label={cardMuted[project.id] === false ? 'Mute video' : 'Unmute video'}
                            >
                              {cardMuted[project.id] === false ? (
                                <FaVolumeUp aria-hidden />
                              ) : (
                                <FaVolumeMute aria-hidden />
                              )}
                            </button>
                          </div>
                        ) : currentImage ? (
                          <>
                            {!mediaReady[project.id] && (
                              <div className="project-image-placeholder project-media-loading">
                                <div className="placeholder-content">
                                  <span>Loading</span>
                                </div>
                              </div>
                            )}
                            <img
                              src={currentImage}
                              alt={project.title}
                              className="project-image"
                              style={{ opacity: mediaReady[project.id] ? 1 : 0 }}
                              onLoad={() => markMediaReady(project.id)}
                            />
                          </>
                        ) : (
                          <div className="project-image-placeholder">
                            <div className="placeholder-content">
                              <FaClock className="placeholder-icon" />
                              <span>Coming Soon</span>
                            </div>
                          </div>
                        )}

                        <div className="project-overlay">
                          <div className="project-actions">
                            {project.video && (
                              <button className="action-btn video-btn" onClick={() => openVideo(project.video)}>
                                <span className="btn-icon">▶</span>
                                Play Video
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="project-info">
                        <h3>{project.title}</h3>

                        {/* Project metadata */}
                        <div className="project-metadata">
                          {project.year && (
                            <span className="metadata-badge year-badge">
                              <FaCalendarAlt className="metadata-icon" aria-hidden />
                              {project.year}
                            </span>
                          )}
                          {project.cityOrSeason && (
                            <span className="metadata-badge location-badge">
                              <FaMapMarkerAlt className="metadata-icon" aria-hidden />
                              {project.cityOrSeason}
                            </span>
                          )}
                        </div>

                        <p className="project-description">
                          {project.description || 'An immersive horror experience crafted by Studio 13'}
                        </p>

                        <div className="project-card-footer">
                          {showViewGallery && (
                            <button
                              type="button"
                              className="project-view-gallery"
                              onClick={openProjectGallery}
                            >
                              <FaImages className="project-view-gallery-icon" aria-hidden />
                              View gallery
                            </button>
                          )}
                          {project.path && (
                            <a
                              href={project.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="project-explore-link"
                            >
                              Explore
                              <span className="explore-arrow">→</span>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <MobileScrollRail scrollerRef={scrollContainerRef} />

              <button className="scroll-arrow scroll-right" onClick={() => scroll('right')} aria-label="Scroll right">
                <FaChevronRight />
              </button>
            </div>
          ) : (
            <div className="no-projects">
              <p>No projects available at the moment.</p>
            </div>
          )}
          </div>
        </div>
      </section>

      {/* Award Winning Attractions Section */}
      <section className="awards-section">
        <div className={`awards-wrapper animate-on-scroll ${visibleElements.has('awards') ? 'animate-fade-in-up' : ''}`} id="awards">
          <div className="awards-card">
            <div className="awards-grid">
              {caseStudies.map((cs) => (
                <div
                  key={cs.slug}
                  className="award-item"
                  onClick={() => setActiveGallery(cs)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${cs.title} gallery`}
                >
                  <div className="award-image">
                    {!mediaReady[`award-${cs.slug}`] && (
                      <div className="project-image-placeholder project-media-loading">
                        <div className="placeholder-content">
                          <span>Loading</span>
                        </div>
                      </div>
                    )}
                    <img
                      src={cs.img}
                      alt={cs.title}
                      style={{ opacity: mediaReady[`award-${cs.slug}`] ? 1 : 0 }}
                      onLoad={() => markMediaReady(`award-${cs.slug}`)}
                    />
                    <div className="award-hover">
                      <span className="award-hover-text">View Gallery</span>
                    </div>
                  </div>
                  <p className="award-caption">Case Study: {cs.title}, {cs.location}</p>
                </div>
              ))}
            </div>
            <h2 className="awards-title">Award Winning Attractions</h2>
          </div>
          <div className="awards-logo">
            <img src={studioLogo} alt="Studio 13 Logo" />
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="news-section">
        <div className="container">
          <News />
        </div>
      </section>

      {/* Giga Project Section */}
      <section className="giga-section">
        <div className="container">
          <GigaProject />
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <Gallery />
        </div>
      </section>

      {/* Video Lightbox */}
      {videoSrc && (
        <div className="video-lightbox" onClick={closeVideo}>
          <div className="video-popup" onClick={(e) => e.stopPropagation()}>
             <video controls autoPlay muted playsInline className="video-full">
              <source src={videoSrc} type={getVideoMimeType(videoSrc)} />
              Your browser does not support the video tag.
            </video>
            <button className="close-video" onClick={closeVideo}>&times;</button>
          </div>
        </div>
      )}

      {/* Project Gallery Modal */}
      {activeGallery && (
        <ProjectGallery
          project={activeGallery}
          onClose={() => setActiveGallery(null)}
        />
      )}
    </div>
  );
};

export default Projects;
